import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Crown, CheckCircle2, Sparkles } from 'lucide-react';
import { Button } from './ui/button';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from './ui/dialog';
import { motion } from 'framer-motion';

interface PremiumModalProps {
    open: boolean;
    onClose: () => void;
    onUpgrade?: () => void;
}

const PremiumModal: React.FC<PremiumModalProps> = ({ open, onClose, onUpgrade }) => {
    const navigate = useNavigate();

    const handleUpgrade = () => {
        if (onUpgrade) {
            onUpgrade();
        } else {
            navigate('/pricing');
            onClose();
        }
    };

    const features = [
        "Access to 900+ spreadsheet challenges",
        "Guided learning paths",
        "Achievement badges",
        "Leaderboard competition",
        "Early access to new features"
    ];

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-md">
                <DialogHeader className="flex flex-col items-center gap-4">
                    <motion.div
                        initial={{ scale: 0.8, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ duration: 0.3 }}
                        className="relative"
                    >
                        <div className="absolute -inset-1 bg-gradient-to-r from-[#3eb489] to-[#4ade80] rounded-full blur opacity-20"></div>
                        <div className="relative rounded-full bg-[#3eb489]/10 dark:bg-[#4ade80]/10 p-4">
                            <Crown className="h-8 w-8 text-[#3eb489] dark:text-[#4ade80]" />
                        </div>
                    </motion.div>
                    <DialogTitle className="text-2xl font-bold text-center">
                        Unlock Premium Features
                    </DialogTitle>
                    <DialogDescription className="text-center space-y-4">
                        <p className="text-lg">
                            Take your spreadsheet skills to the next level
                        </p>
                        <div className="space-y-2">
                            {features.map((feature, index) => (
                                <motion.div
                                    key={index}
                                    initial={{ opacity: 0, x: -20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: index * 0.1 }}
                                    className="flex items-center gap-2 text-gray-600 dark:text-gray-300"
                                >
                                    <CheckCircle2 className="h-5 w-5 text-[#3eb489] dark:text-[#4ade80]" />
                                    {feature}
                                </motion.div>
                            ))}
                        </div>
                        <div className="pt-4">
                            <p className="text-2xl font-bold text-[#3eb489] dark:text-[#4ade80]">
                                $10<span className="text-lg font-normal">/month</span>
                            </p>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                                Cancel anytime
                            </p>
                        </div>
                    </DialogDescription>
                </DialogHeader>
                <DialogFooter className="flex flex-col-reverse sm:flex-row sm:justify-center sm:space-x-2 sm:space-y-0 space-y-2">
                    <Button
                        variant="outline"
                        onClick={onClose}
                        className="w-full"
                    >
                        Maybe Later
                    </Button>
                    <Button
                        onClick={handleUpgrade}
                        className="w-full bg-[#3eb489] hover:bg-[#35a07a] dark:bg-[#4ade80] dark:hover:bg-[#22c55e] text-white"
                    >
                        <Sparkles className="h-4 w-4 mr-2" />
                        Upgrade to Premium
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default PremiumModal; 